import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  input,
  OnDestroy,
  Output,
  Provider,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { BfxSoundsService } from "../bfx-common/services/bfx-sounds.service";
import { BfxPopupEventSetting } from "./model/bfx-popup-event-setting";

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BfxAppEventSetting } from "app/bfx-user-settings/model/bfx-app-event-setting";
import {
  BfxUserNotificationSettingsService,
  SettingAudioType,
  SettingFilterType,
} from "app/bfx-user-settings/services/bfx-user-notification-settings.service";
import {
  DEFAULT_CHANGE_CALLBACK,
  DEFAULT_TOUCH_CALLBACK,
  OnChangeCallbackType,
  OnTouchCallbackType,
} from "shared/base-components/models/ControlValueAccessorTypes";
import {
  BfxAutoCompleteComponent,
  BfxAutoCompleteComponentState,
} from "app/bfx-element-library/bfx-autocomplete.component";
import { Subscription } from "rxjs";

type SettingDataType = {
  eventIdentifier: string;
  enabled: boolean;
  returnOnLogin: boolean;
  audio: SettingAudioType | string;
  filter: SettingFilterType | string;
  filterList: SettingFilterType[];
};

export const VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxNotificationSettingRowComponent),
  multi: true,
};

@Component({
  selector: "bfx-notification-setting",
  templateUrl: "./html/bfx-notification-setting-row.html",
  providers: [VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxNotificationSettingRowComponent implements ControlValueAccessor, OnDestroy {
  /** ControlValueAccessor callback */
  public onModelChange: OnChangeCallbackType = DEFAULT_CHANGE_CALLBACK;

  /** ControlValueAccessor callback */
  public onModelTouched: OnTouchCallbackType = DEFAULT_TOUCH_CALLBACK;

  public audioSuggestions: SettingAudioType[] = [];
  public filterSuggestions: SettingFilterType[] = [];

  public data: SettingDataType;

  private origValue: BfxPopupEventSetting | BfxAppEventSetting;

  private filterUpdateSubscription: Subscription;

  public isAppSetting: boolean = false;

  public readonly audioDisabled = input<boolean>(false);

  @Output() public validChanged: EventEmitter<boolean> = new EventEmitter();

  @ViewChildren(BfxAutoCompleteComponent)
  private autoCompletes: QueryList<BfxAutoCompleteComponent>;

  constructor(
    private settingsService: BfxUserNotificationSettingsService,
    private soundsService: BfxSoundsService,
    private cd: ChangeDetectorRef,
  ) {
    this.data = this.createEmptySetting();
  }

  ngOnDestroy(): void {
    if (this.filterUpdateSubscription) {
      this.filterUpdateSubscription.unsubscribe();
    }
  }

  protected createEmptySetting(): SettingDataType {
    return {
      eventIdentifier: "",
      enabled: false,
      returnOnLogin: false,
      audio: null,
      filter: null,
      filterList: [],
    };
  }

  /**
   * ControlValueAccessor interface method
   * @param fn
   */
  public registerOnChange(fn: OnChangeCallbackType): void {
    this.onModelChange = fn;
  }

  /**
   * ControlValueAccessor interface method
   * @param fn
   */
  public registerOnTouched(fn: OnTouchCallbackType): void {
    this.onModelTouched = fn;
  }

  /**
   * CReate data model for popup setting
   * @param origSetting
   */
  private initializePopupInputData(origSetting: BfxPopupEventSetting) {
    this.origValue = origSetting;
    this.settingsService.ready.then(() => {
      let audio = this.settingsService.findSound(origSetting.audio?.label);
      const filterList = this.settingsService.getFilterList(origSetting.type);
      let filterMatch = filterList.find((filter) => filter.id == origSetting.filter?.id);
      if (!filterMatch) {
        filterMatch = this.settingsService.getNoFilter();
      }
      this.data = Object.assign({}, origSetting, {
        audio,
        filter: filterMatch,
        filterList,
        returnOnLogin: false,
      });
      this.filterSuggestions = filterList;
      this.audioSuggestions = this.settingsService.getSoundsList();
      // this.filterSuggestions = null;
      // this.audioSuggestions = null;

      this.cd.markForCheck();
      this.filterUpdateSubscription = this.settingsService.$filtersUpdated.subscribe(() => {
        this.updateFilterList();
      });
    });
  }

  /**
   * Create data model for app setting
   * @param origSetting
   */
  private initializeAppInputData(origSetting: BfxAppEventSetting) {
    this.origValue = origSetting;
    this.settingsService.ready.then(() => {
      let audio = this.settingsService.findSound(origSetting.audio?.label);
      const filterList = this.settingsService.getAppFilterList(origSetting.type);
      let filterMatch = filterList.find((filter) => filter.id == origSetting.filter?.id);
      if (!filterMatch) {
        filterMatch = this.settingsService.getNoFilter();
      }
      this.data = Object.assign({}, origSetting, {
        audio,
        filter: filterMatch,
        filterList,
      });
      this.filterSuggestions = filterList;
      this.audioSuggestions = this.settingsService.getSoundsList();
      this.cd.markForCheck();

      this.filterUpdateSubscription = this.settingsService.$filtersUpdated.subscribe(() => {
        this.updateFilterList();
      });
    });
  }

  /**
   * After updating of any filter, reread the filter list for this filter type and check the currently
   * selected value
   */
  private updateFilterList() {
    let filterList: SettingFilterType[];
    let filterMatch: SettingFilterType;
    if (this.origValue instanceof BfxPopupEventSetting) {
      filterList = this.settingsService.getFilterList(this.origValue.type);
      filterMatch = filterList.find(
        (filter) => filter.id == (this.data.filter as SettingFilterType)?.id,
      );
    } else {
      filterList = this.settingsService.getAppFilterList(this.origValue.type);
      filterMatch = filterList.find(
        (filter) => filter.id == (this.data.filter as SettingFilterType)?.id,
      );
    }
    if (!filterMatch) {
      filterMatch = this.settingsService.getNoFilter();
    }
    this.data.filter = filterMatch;
    this.data.filterList = filterList;
    this.filterSuggestions = filterList;
    this.cd.markForCheck();
  }

  /**
   * Called when auto writing a value to this view component from an external model
   * @param value
   */
  public writeValue(value: BfxPopupEventSetting | BfxAppEventSetting): void {
    if (value instanceof BfxPopupEventSetting) {
      this.initializePopupInputData(value);
      this.isAppSetting = false;
    } else if (value instanceof BfxAppEventSetting) {
      this.initializeAppInputData(value);
      this.isAppSetting = true;
    }
  }

  public pageDataChanged() {
    if (this.validate()) {
      this.onModelChange(this.getUpdatedValue());
    } else {
      this.validChanged.emit(false);
    }
    this.cd.markForCheck();
  }

  public searchAudios(event) {
    this.audioSuggestions = this.settingsService.searchAudios(event);
    this.cd.markForCheck();
    //this.onDataChanged(event, this.audioSuggestions);
  }

  public onChangeAudio(audio: SettingAudioType) {
    const changed = this.data.audio !== audio;
    if (changed) {
      this.data.audio = audio;
      this.pageDataChanged();
      if (audio.hasOwnProperty("label") && audio.label != "None" && this.audioDisabled() === false)
        this.soundsService.playSound(audio.label, audio.value);
    }
  }

  public onBlurAudio($event) {
    if ((this.data.audio == null || this.data.audio === "") && this.audioSuggestions?.length > 0) {
      setTimeout(() => {
        this.data.audio = this.audioSuggestions[0];
        this.cd.markForCheck();
      }, 100);
    }
    this.pageDataChanged();
  }

  public searchFilters(event) {
    const query: string = event.query.toLowerCase();
    this.filterSuggestions = this.data.filterList.filter(
      (a) => a.name.toLowerCase().indexOf(query) >= 0,
    );
    this.cd.markForCheck();
    //this.onDataChanged(event, this.audioSuggestions);
  }

  public onSelectFilter($event) {
    this.pageDataChanged();
  }

  public onBlurFilter($event) {
    if (this.data.filter == null || this.data.filter === "") {
      setTimeout(() => {
        this.data.filter = this.data.filterList[0];
        this.cd.markForCheck();
      }, 100);
    }
    this.pageDataChanged();
  }

  private getUpdatedValue(): BfxPopupEventSetting | BfxAppEventSetting {
    const retValue = Object.assign({}, this.origValue, {
      enabled: this.data.enabled,
      filter: this.data.filter,
      audio: this.data.audio,
      returnOnLogin: this.data.returnOnLogin,
    });
    return retValue;
  }

  public validate(): boolean {
    let valid: boolean = true;
    if (!this.data.filter || !this.data.filter.hasOwnProperty("name")) {
      valid = false;
    }
    if (!this.data.audio || !this.data.audio.hasOwnProperty("value")) {
      valid = false;
    }
    return valid;
  }

  /**
   * Will hide any open dropdowns visible - used when scrolling occurs in parent component so that dropdown
   * position does not get out of sync with the autocomplete
   */
  public closeDropdowns() {
    for (const autocomplete of this.autoCompletes) {
      if (
        autocomplete.state === BfxAutoCompleteComponentState.ACTIVE &&
        autocomplete.panelVisible
      ) {
        logger.out.info("nsr-c - Got closeDropdowns for active autocomplete");
        autocomplete.hide();
      }
    }
  }
}
